import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import DOMPurify from 'dompurify';

import ClearTeamAssistAnswer from 'actions/team_assist/clear_team_assist_answer';
import FetchTeamAssistAnswer from 'actions/team_assist/fetch_team_assist_answer';
import RetryTeamAssist from 'actions/team_assist/retry_team_assist';
import UseTeamAssistResponse from 'actions/team_assist/use_team_assist_response';
import Button, { ButtonTypes } from 'components/common/button';
import SafeHtml from 'components/common/safe_html';
import Tooltip from 'components/common/tooltip';
import TeamAssistAnswerPreviewModal from 'components/customer/team_assist/team_assist_answer_preview_modal';
import { TeamAssistLoadingDots } from 'components/customer/team_assist/team_assist_shared';
import { useExecuteAction } from 'components/hooks/connect_hooks';
import TeamAssistMessage from 'models/team_assist_message';

function MessageBubble({ msg, onUse }) {
  const executeAction = useExecuteAction();
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [previewAnswerId, setPreviewAnswerId] = useState(null);
  const [isUseClicked, setIsUseClicked] = useState(false);
  const hasCitations = msg.citations && msg.citations.length > 0;
  const displayContent = formatMessageContent(
    hasCitations ? replaceCitationRefsInContent(msg.content, msg.citations) : msg.content
  );

  const handleCopy = (content, messageId) => {
    const plainText = getPlainTextFromHtml(content);
    const html = content || '';
    if (!plainText && !html.trim()) return;

    const writeClipboard = () => {
      const item = {
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
        'text/html': new Blob([html], { type: 'text/html' }),
      };
      return navigator.clipboard.write([new window.ClipboardItem(item)]);
    };

    writeClipboard()
      .then(() => {
        setCopiedMessageId(messageId);
        setTimeout(() => setCopiedMessageId(null), 2000);
      })
      .catch(() => {
        navigator.clipboard.writeText(plainText).then(() => {
          setCopiedMessageId(messageId);
          setTimeout(() => setCopiedMessageId(null), 2000);
        });
      });
  };

  if (msg.role === TeamAssistMessage.Role.agent_question) {
    return (
      <UserRow>
        <UserBubbleSpacer />
        <UserMessageContent>
          <UserBubble>
            <SafeHtml html={formatMessageContent(msg.displayedMessage || msg.content)} />
          </UserBubble>
        </UserMessageContent>
      </UserRow>
    );
  }

  const isCopied = copiedMessageId === msg.id;
  const copyButton = (
    <Tooltip delay={250} message={isCopied ? 'Copied' : 'Copy'} position="bottom" style={{ padding: '5px 10px' }}>
      <CopyButton
        aria-label={isCopied ? 'Copied response' : 'Copy response'}
        data-aid="teamAssistCopyMessageButton"
        isCopied={isCopied}
        onClick={() => handleCopy(displayContent, msg.id)}
        type="button"
      >
        <i aria-hidden="true" className={isCopied ? 'fa fa-check' : 'fa fa-copy'} />
      </CopyButton>
    </Tooltip>
  );

  if (msg.type === TeamAssistMessage.Type.suggested_response) {
    return (
      <AssistantRow>
        <AssistantMessageWrapper>
          <AssistantContent>
            {msg.isRetrying ? (
              <TeamAssistLoadingDots />
            ) : (
              <>
                <SuggestedResponseContent>
                  <SafeHtml html={displayContent || ''} />
                </SuggestedResponseContent>
                <ReviewReasons reviewReasons={msg.reviewReasons} />
                <SuggestedResponseActions>
                  <UseButton
                    aria-label="Use suggested response"
                    buttonType={ButtonTypes.PRIMARY}
                    disabled={isUseClicked}
                    isSmall
                    onClick={() => {
                      setIsUseClicked(true);
                      executeAction(UseTeamAssistResponse, {
                        customerId: msg.customerId,
                        messageId: msg.id,
                      });
                      onUse?.();
                    }}
                    type="button"
                  >
                    Use
                  </UseButton>
                  <Tooltip delay={250} message="Retry" position="bottom" style={{ padding: '5px 10px' }}>
                    <RetryIconButton
                      aria-label="Retry suggested response"
                      onClick={() =>
                        executeAction(RetryTeamAssist, {
                          customerId: msg.customerId,
                          messageId: msg.id,
                        })
                      }
                      type="button"
                    >
                      <i aria-hidden="true" className="fa fa-repeat" />
                    </RetryIconButton>
                  </Tooltip>
                  <SuggestedResponseCopy>{copyButton}</SuggestedResponseCopy>
                </SuggestedResponseActions>
                <Citations
                  citations={msg.citations}
                  onCitationClick={answerId => {
                    setPreviewAnswerId(answerId);
                    executeAction(FetchTeamAssistAnswer, { answerId });
                  }}
                  onClosePreview={() => {
                    setPreviewAnswerId(null);
                    executeAction(ClearTeamAssistAnswer);
                  }}
                  previewAnswerId={previewAnswerId}
                />
              </>
            )}
          </AssistantContent>
        </AssistantMessageWrapper>
      </AssistantRow>
    );
  }

  return (
    <AssistantRow>
      <AssistantMessageWrapper>
        <AssistantContent>
          <SuggestedResponseContent>
            <SafeHtml html={displayContent || ''} />
          </SuggestedResponseContent>
          <ReviewReasons reviewReasons={msg.reviewReasons} />
          <Citations
            citations={msg.citations}
            onCitationClick={answerId => {
              setPreviewAnswerId(answerId);
              executeAction(FetchTeamAssistAnswer, { answerId });
            }}
            onClosePreview={() => {
              setPreviewAnswerId(null);
              executeAction(ClearTeamAssistAnswer);
            }}
            previewAnswerId={previewAnswerId}
          />
          <AssistantActions>{copyButton}</AssistantActions>
        </AssistantContent>
      </AssistantMessageWrapper>
    </AssistantRow>
  );
}

MessageBubble.propTypes = {
  msg: PropTypes.shape({
    citations: PropTypes.arrayOf(
      PropTypes.shape({
        snippetId: PropTypes.string,
        snippetName: PropTypes.string,
      })
    ),
    confidence: PropTypes.number,
    content: PropTypes.string,
    customerId: PropTypes.string,
    displayedMessage: PropTypes.string,
    id: PropTypes.string,
    isRetrying: PropTypes.bool,
    reviewReasons: PropTypes.arrayOf(PropTypes.string),
    role: PropTypes.string,
    type: PropTypes.string,
  }),
  onUse: PropTypes.func,
};

export default MessageBubble;
export { AssistantRow };

function ReviewReasons({ reviewReasons }) {
  if (!reviewReasons || reviewReasons.length === 0) {
    return null;
  }

  return (
    <ReviewReasonsContainer>
      <ReviewReasonsIcon className="fa fa-exclamation-triangle" />
      <ReviewReasonsText>
        <ReviewReasonsLabel>Needs review</ReviewReasonsLabel>
        {reviewReasons.map((reason, idx) => (
          <ReviewReason key={idx}>{reason}</ReviewReason>
        ))}
      </ReviewReasonsText>
    </ReviewReasonsContainer>
  );
}

ReviewReasons.propTypes = {
  reviewReasons: PropTypes.arrayOf(PropTypes.string),
};

function Citations({ citations, onCitationClick, onClosePreview, previewAnswerId }) {
  if (!citations || citations.length === 0) {
    return null;
  }

  return (
    <>
      <CitationsDivider />
      <CitationsLabel>Answers referenced</CitationsLabel>
      <CitationsList>
        {citations.map((citation, idx) => (
          <CitationLink
            aria-label={`Open referenced answer ${citation.snippetName || citation.snippetId}`}
            href="#"
            key={citation.snippetId}
            onClick={evt => {
              evt.preventDefault();
              onCitationClick(citation.snippetId);
            }}
          >
            <CitationNumber>[{idx + 1}]</CitationNumber> {citation.snippetName || citation.snippetId}
          </CitationLink>
        ))}
      </CitationsList>
      {previewAnswerId && <TeamAssistAnswerPreviewModal onClose={onClosePreview} />}
    </>
  );
}

Citations.propTypes = {
  citations: PropTypes.arrayOf(
    PropTypes.shape({
      snippetId: PropTypes.string,
      snippetName: PropTypes.string,
    })
  ),
  onCitationClick: PropTypes.func.isRequired,
  onClosePreview: PropTypes.func.isRequired,
  previewAnswerId: PropTypes.string,
};

function getPlainTextFromHtml(html) {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = DOMPurify.sanitize(html);
  return div.textContent || div.innerText || '';
}

function formatMessageContent(content) {
  if (!content) {
    return '';
  }
  if (containsHtml(content)) {
    return content;
  }
  return escapeHtml(content).replace(/\r\n|\r|\n/g, '<br />');
}

// Note: may false-positive on plain text with angle brackets (e.g. "x < y and z > 0"),
// but this is unlikely in practice since message content is either plain text or HTML from the backend.
function containsHtml(content) {
  return /<\/?[a-z][\s\S]*>/i.test(content);
}

function escapeHtml(content) {
  return content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function replaceCitationRefsInContent(html, citations) {
  if (!html || !citations || citations.length === 0) return html;

  let result = html;
  citations.forEach((citation, idx) => {
    const num = idx + 1;
    const escapedId = citation.snippetId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const sup = `<sup class="cite-ref">[${num}]</sup>`;
    // Match patterns like (snippetId – Name) or (snippetId - Name), optionally wrapped in <em>/<i> tags
    const pattern = new RegExp(`(?:<(?:em|i)>)?\\(?${escapedId}(?:\\s*[–—-]\\s*[^)<]*)?\\)?(?:</(?:em|i)>)?`, 'g');
    result = result.replace(pattern, sup);
  });
  return result;
}

const UserBubbleSpacer = styled.div`
  flex-shrink: 0;
  width: 22px;
`;

const UserRow = styled.div`
  align-self: flex-end;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
  max-width: 100%;
  width: 100%;
`;

const AssistantRow = styled.div`
  align-self: flex-start;
  display: flex;
  justify-content: flex-start;
  margin-bottom: 12px;
  max-width: 100%;
  width: 100%;
`;

const AssistantMessageWrapper = styled.div`
  align-items: stretch;
  display: flex;
  max-width: 100%;
  width: 100%;
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.gray500};
  cursor: pointer;
  flex-shrink: 0;
  font-size: 16px;
  height: 16px;
  line-height: 1;
  margin-top: 2px;
  padding: 0;
  width: 16px;

  &:hover {
    color: ${p => p.theme.colors.gray700};
  }
`;

const UserBubble = styled.div`
  background: ${p => p.theme.colors.gray100};
  border-radius: 12px 2px 12px 12px;
  color: ${p => p.theme.colors.black};
  font-size: ${p => p.theme.fontSize.base};
  line-height: 1.4;
  max-width: 100%;
  padding: 8px 12px;
  word-wrap: break-word;
`;

const AssistantContent = styled.div`
  color: ${p => p.theme.colors.black};
  flex: 1;
  font-size: ${p => p.theme.fontSize.base};
  line-height: 1.4;
  min-width: 0;
  word-wrap: break-word;
`;

const AssistantActions = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 8px;
`;

const UserMessageContent = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 16px;
`;

const SuggestedResponseContent = styled.div`
  p {
    margin: 0 0 8px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
  ul,
  ol {
    margin: 4px 0;
    padding-left: 20px;
  }
  sup.cite-ref {
    color: ${p => p.theme.colors.purple400};
    font-size: 10px;
    font-weight: 600;
  }
`;

const SuggestedResponseActions = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: flex-start;
  padding-top: 8px;
`;

const SuggestedResponseCopy = styled.div`
  display: flex;
`;

const UseButton = styled(Button)`
  min-width: 64px;
`;

const RetryIconButton = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.gray500};
  cursor: pointer;
  flex-shrink: 0;
  font-size: 16px;
  height: 16px;
  line-height: 1;
  margin-top: 1px;
  padding: 0;
  width: 16px;

  &:hover {
    color: ${p => p.theme.colors.gray700};
  }
`;

const ReviewReasonsContainer = styled.div`
  align-items: flex-start;
  background: ${p => p.theme.colors.yellow100};
  border-radius: 4px;
  display: flex;
  gap: 6px;
  margin-top: 8px;
  padding: 6px 8px;
`;

const ReviewReasonsIcon = styled.i`
  color: ${p => p.theme.colors.yellow600};
  flex-shrink: 0;
  font-size: 12px;
  margin-top: 2px;
`;

const ReviewReasonsText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ReviewReasonsLabel = styled.span`
  color: ${p => p.theme.colors.yellow700};
  font-size: 11px;
  font-weight: 600;
`;

const ReviewReason = styled.span`
  color: ${p => p.theme.colors.yellow600};
  font-size: 11px;
  line-height: 1.3;
`;

const CitationsDivider = styled.hr`
  border: none;
  border-top: 1px solid ${p => p.theme.colors.gray300};
  height: 0;
  margin: 12px 0 8px 0;
`;

const CitationsLabel = styled.span`
  color: ${p => p.theme.colors.gray600};
  font-size: 12px;
  margin-bottom: 4px;
`;

const CitationsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  gap: 4px 12px;
  line-height: 1.3;
`;

const CitationNumber = styled.span`
  font-weight: 600;
`;

const CitationLink = styled.a`
  color: ${p => p.theme.colors.green400};
  text-decoration: none;

  &:hover {
    color: ${p => p.theme.colors.green300};
  }
`;
